// React
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Redux
import { useSelector } from "react-redux";

// MUI
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";

//Components
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";

// Third party
import { compact } from "lodash-es";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Helpers
import { accountFormat } from "@/Common/helpers/number";
import { getPositionsByDepartments } from "@/Common/helpers/Positions";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { and } from "@/Common/helpers/sonar";
import {
	dateValidation,
	emptyValidation,
	overlapsValidation,
} from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { errorLogger } from "@Common/helpers/logger";

// Constants
import actionsList from "@/Constants/actionsList";
import { DATE_FORMAT } from "@/Constants/date";
import { COLUMN_RESIZE_FACTOR, ONE_HUNDRED_FIFTY_FIVE, ONE_HUNDRED_SIXTY, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { filteredRowsByCommissionCyle } from "@/Motivators/context/Common/MotivatorsCommon";
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";
import { useSectionMotivators } from "@/Motivators/context/Common/MotivatorsSectionContext";

// APIs
import { getUsersSelect } from "@/Motivators/api/common";

// Components
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TableInputValue from "@/Layouts/components/InputComponents/TableInputValue";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import PayoutFrequencySelector from "../PayoutFrequencySelector";

// Icons
import { PayoutFrequencyOptions } from "@/Common/constants/PayoutFrequencyOptions";
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";
import { useDrawModalities } from "@/Settings/hooks/useDrawModalities";
import { tableSmartSelectorClasses, tableSmartSelectorListBoxProps, tableSmartSelectorPopperProps } from "@/styles/TableSmartSelector";
import CancelIcon from "@mui/icons-material/Close";

InputEditUserCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	setAssocAlreadySelected: PropTypes.func,
	error: PropTypes.bool,
	cycleOverlapError: PropTypes.bool,
	rows: PropTypes.array,
	row: PropTypes.shape({
		nameAssociate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	}),
	setCycleOverlapError: PropTypes.func,
	department: PropTypes.string,
};

function InputEditUserCell({ id, field, error, department, row, rows, setAssocAlreadySelected, setCycleOverlapError }) {
	const apiRef = useGridApiContext();
	const locationId = useLocationId();
	const [recipient, setRecipient] = useState(() => row.nameAssociate);

	const positions =
		department === "service"
			? getPositionsByDepartments(["Parts", "Service", "Body Shop"])
			: getPositionsByDepartments(["Sales", "Finance"]);

	const { data, isLoading } = useQuery({
		queryKey: ["recipients", locationId, positions],
		queryFn: async () => getUsersSelect(locationId, positions),
		staleTime: Infinity,
		enabled: !!positions?.length,
		placeholderData: [],
	});

	const handleChange = (userSelected) => {
		if (userSelected) {
			const alreadySelected = rows.find((row) => row.nameAssociate?._id === userSelected._id);
			if (!alreadySelected) {
				setCycleOverlapError(false);
			}
			setAssocAlreadySelected(alreadySelected);
			apiRef.current.setEditCellValue({ id, field, value: userSelected });
			apiRef.current.setEditCellValue({ id, field: "payoutFrequency", value: null });
			setRecipient(userSelected);
		}
	};

	return (
		<SmartSelector
			showBy="fullName"
			sortBy="fullName"
			options={data}
			selected={recipient}
			inputProps={{
				placeholder: "Recipients",
				classes: tableSmartSelectorClasses,
			}}
			listBoxProps={tableSmartSelectorListBoxProps}
			popperProps={tableSmartSelectorPopperProps}
			error={error && !recipient}
			loading={isLoading}
			onChangeAction={handleChange}
		/>
	);
}

const formatDate = (val) => moment.utc(val).format(DATE_FORMAT.monthAbbrDatePadded);

const minDate = ({ type, ccStartDate, fromDate, masterPermission = false, temporaryGuaranteedStartDate = null }) => {
	const today = moment();
	const startDateVal = moment(moment.utc(ccStartDate).format(DATE_FORMAT.monthAbbrDatePadded)).startOf("day");

	if (type === "to") {
		return moment(fromDate);
	} else if (masterPermission && temporaryGuaranteedStartDate) {
		return moment.max(temporaryGuaranteedStartDate, ccStartDate);
	} else if (startDateVal.isAfter(today)) {
		return startDateVal;
	} else {
		return today;
	}
};

const reformatDate = (val) => moment(getFormattedDate(val, "", DATE_FORMAT.monthAbbrDatePadded)).startOf("day");

const sortPayPeriodsDate = (tempGuaranteeData) => {
	if (!Array.isArray(tempGuaranteeData)) {
		return [];
	}
	return tempGuaranteeData.flatMap((period) => period.payPeriods).sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
};

const shouldDisableDateFrom = ({ date, endDate, startDate, payPeriodsDate }) => {
	const isDifferentOfAllPayPeriodsStartDate = payPeriodsDate.every(
		(payPeriod) =>
			!moment.utc(payPeriod.startDate).isSame(reformatDate(date), "day") ||
			moment.utc(date).isBefore(reformatDate(startDate), "day") ||
			moment.utc(date).isAfter(reformatDate(endDate), "day")
	);
	return isDifferentOfAllPayPeriodsStartDate;
};

const shouldDisableDateTo = ({ date, endDate, startDate, payPeriodsDate }) => {
	const isDifferentOfAllPayPeriodsEndDate = payPeriodsDate.every(
		(payPeriod) =>
			!moment.utc(payPeriod.endDate).isSame(reformatDate(date), "day") ||
			moment.utc(date).isBefore(reformatDate(startDate), "day") ||
			moment.utc(date).isAfter(reformatDate(endDate), "day")
	);

	return isDifferentOfAllPayPeriodsEndDate;
};

function InputEditDatetimeCell(insideProps) {
	const {
		id,
		field,
		startDate,
		endDate,
		fromDate,
		type,
		error,
		value,
		selectedItem,
		setEffecDateFrom,
		setEffecDateTo,
		valueOverlaps,
		apiRef,
		setCycleOverlapError,
		cycleOverlapError,
		queryClient,
		locationId,
		disabled = false,
		masterPermission = false,
		department,
	} = insideProps;

	const [dateValue, setDateValue] = useState(insideProps?.value ? new Date(insideProps.value) : insideProps?.value);

	const onChangeHandle = (dataValue) => {
		const inputValue = formatDate(dataValue);
		apiRef.current.setEditCellValue({ id, field, value: dataValue, debounceMs: 200 });

		setDateValue(inputValue);
		setCycleOverlapError(false);

		if (type === "from") {
			setEffecDateFrom(inputValue);
		}

		if (type === "to") {
			setEffecDateTo(inputValue);
		}
	};

	const ccStartDate = moment.utc(selectedItem?.commissionCycleStartDate).startOf("month");
	const valueExists = emptyValidation(value);

	const minDateTime = minDate({ type, ccStartDate, fromDate, masterPermission, temporaryGuaranteedStartDate: startDate });

	return (
		<DataTableDateTimePicker
			disableHighlightToday
			hideTabs
			value={dateValue}
			minDateTime={minDateTime}
			maxDateTime={moment(formatDate(endDate)).endOf("month")}
			defaultCalendarMonth={moment(formatDate(minDateTime)).startOf("day")}
			disableIgnoringDatePartForTimeValidation
			format={DATE_FORMAT.monthAbbrDatePadded}
			views={["year", "month", "day"]}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
			error={!valueExists || error || cycleOverlapError}
			onChange={onChangeHandle}
			shouldDisableDate={(date) => {
				const queryKey = department === "service" ? "temporaryGuaranteedSidebar" : "salesTemporaryGuaranteedSidebar";
				const temporaryGuaranteedData = queryClient.getQueryData([queryKey, locationId]);
				const payPeriodArr = temporaryGuaranteedData?.filter((item) => {
					const isStartDateValid = moment.utc(reformatDate(item.startDate)).isSameOrAfter(reformatDate(startDate), "day");
					const isEndDateValid = moment.utc(reformatDate(item.startDate)).isSameOrBefore(reformatDate(endDate), "day");
					return isStartDateValid && isEndDateValid;
				});
				const sortedDates = sortPayPeriodsDate(payPeriodArr);
				return type === "from"
					? shouldDisableDateFrom({ date, endDate, startDate, payPeriodsDate: sortedDates })
					: shouldDisableDateTo({ date, endDate, startDate, payPeriodsDate: sortedDates });
			}}
			tooltipMessage={valueOverlaps || cycleOverlapError ? "Overlapping Dates" : ""}
			disabled={disabled}
		/>
	);
}
const normalizeId = (id) => (id?.includes("_") ? id.split("_")[1] : id);
const shouldUpdate = (query, motivatorType, props) =>
	query.queryKey[0] === motivatorType + "Entry" &&
	Boolean(query.queryKey[1]) &&
	query.queryKey[1].includes(normalizeId(props?.selectedItem?._id));

export default function TemporaryGuaranteedTable(props) {
	const apiRef = useGridApiRef();
	const queryClient = useQueryClient();

	const { timezone } = useLocation();

	const { locationId, mutations, subscriptions, motivatorType, isLoading, identifiers } = useMotivators();
	const { setTableApiRef, setTableRows } = useSectionMotivators();
	const [assocAlreadySelected, setAssocAlreadySelected] = useState();

	// States
	const [rows, setRows] = useState([]);
	const [rowModesModel, setRowModesModel] = useState({});
	const [selectedRows, setSelectedRows] = useState([]);
	const [effecDateFrom, setEffecDateFrom] = useState(null);
	const [effecDateTo, setEffecDateTo] = useState(null);
	const [rowLoading, setRowLoading] = useState(false);
	const [cycleOverlapError, setCycleOverlapError] = useState(false);
	const user = useSelector((state) => state.user.user);

	const { drawModalities, isLoadingDrawModalities } = useDrawModalities();

	const newlyAddedRow = useRef(null);
	const lastAddedRow = useRef(null);

	// Auth
	const { useAuth } = useAuthorization();
	const editTempGuaranteed = useAuth(actionsList.EDIT_TEMP_GUARANTEED);

	/********************************************* SUBSCRIPTIONS **********************************************/
	const { getSubscription } = useSubscriptions();
	const tableItemsSubscription = getSubscription([motivatorType + "Body", locationId, normalizeId(props?.selectedItem?._id)]);

	const shouldEdit = () => props.currentMonth && editTempGuaranteed;
	const canAddItemsInTable = and(
		props.canAddItems,
		props?.selectedItem?.commissionCycleStatus !== "FINALIZED",
		props?.selectedItem?.commissionCycleStatus !== "POSTED"
	);

	function setSubscriptionCallback(subscriptionType) {
		tableItemsSubscription?.subscriptions?.[subscriptionType]?.setCallback((result) => {
			const items = result.data?.[subscriptions.get(subscriptionType)?.getter]?.items;

			// Get the row currently in edition mode
			const rowInEditionModeId = Object.keys(rowModesModel).find((key) => rowModesModel[key].mode === GridRowModes.Edit);
			const rowInEditionMode = rows.find((row) => row._id === rowInEditionModeId);
			const queryCache = queryClient.getQueryCache().findAll(motivatorType + "Entry", { exact: false });
			queryCache.forEach((query) => {
				if (shouldUpdate(query, motivatorType, props)) {
					queryClient.setQueryData(query.queryKey, (cachedData) => {
						return {
							...cachedData,
							items,
						};
					});
				}
			});

			// If the row that is currently in edition mode is deleted, clean up the edition mode
			if (subscriptionType === "onDeleteItem" && rowInEditionMode) {
				const wasDeletedRowInEditMode = !Boolean(items.find((item) => item._id === rowInEditionMode._id)?._id);
				cleanUpEditionMode(rowInEditionMode, "delete", wasDeletedRowInEditMode);
			} else {
				cleanUpEditionMode();
				lastAddedRow.current = null;
			}
		});
	}

	// Usage
	setSubscriptionCallback("onCreateItem");
	setSubscriptionCallback("onDeleteItem");
	setSubscriptionCallback("onUpdateItem");

	const getNormalizedCommissionCycle = (value) => (value?.includes("_") ? value.split("_")[1] : value);

	/************************************************** MUTATIONS *******************************************************/
	const { mutate: addTemporaryGuaranteedItemHandler } = useMutation({
		mutationFn: ({ commissionCycleId, input, unitListId }) =>
			mutations.createItem({
				locationId,
				input,
				unitListId,
				commissionCycleId: getNormalizedCommissionCycle(commissionCycleId),
				department: identifiers.department,
			}),
	});

	const { mutate: deleteTemporaryGuaranteedItemHandler } = useMutation({
		mutationFn: ({ commissionCycleId, listId, itemIds }) =>
			mutations.deleteItem({
				locationId,
				itemIds,
				commissionCycleId: getNormalizedCommissionCycle(commissionCycleId),
				listId: listId?.includes("_") ? listId.split("_")[1] : listId,
				department: identifiers.department,
			}),
	});

	const { mutate: updatTemporaryGuaranteedItemHandler } = useMutation({
		mutationFn: ({ unitItemId, input, commissionCycleId }) => {
			return mutations.updateItem({
				locationId,
				input,
				commissionCycleId,
				unitItemId,
				department: identifiers.department,
			});
		},
	});
	/************************************************** END MUTATIONS ****************************************************/
	let rowsFiltered = [];
	rowsFiltered = useMemo(() => {
		if (props?.data) {
			const result = filteredRowsByCommissionCyle({
				parentId: props?.selectedItem?.parentCommissionCycleId,
				ccStartDate: props?.selectedItem?.commissionCycleStartDate,
				ccEndDate: props?.selectedItem?.commissionCycleEndDate,
				rows: props?.data,
			});

			return result;
		}
		return [];
	}, [props?.data, props?.selectedItem]);

	useEffect(() => {
		if (rowsFiltered?.length) {
			const newRow = rows.find((row) => !row.entryDate);
			if (editModeActive) {
				setRows(compact([newRow, ...rowsFiltered]));
				setTableRows(compact([newRow, ...rowsFiltered]));
			} else {
				setTableRows(compact(rowsFiltered));
				setRows(compact(rowsFiltered));
			}
			setTableApiRef(apiRef);

			return () => {
				if (lastAddedRow.current) {
					setRows(lastAddedRow.current);
				} else if (rowsFiltered?.length) {
					setRows([]);
				}
				setTableRows([]);
				setCycleOverlapError(false);
			};
		}
	}, [rowsFiltered]);

	// This it's the correct way to listen to the edit mode changes.
	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const handleAddRow = () => {
		const key = uuidv4();

		const newRow = {
			_id: key,
			updatedAt: new Date(),
			addedBy: user,
			effectiveDateFromItem: "",
			effectiveDateToItem: "",
			amount: 0,
		};

		setAssocAlreadySelected(null);
		setCycleOverlapError(false);
		setRows((oldRows) => [newRow, ...oldRows]);
		setEffecDateFrom(null);
		setEffecDateTo(null);

		newlyAddedRow.current = { ...newRow };

		// Add the new row to the rows state.
		setRowModesModel((oldModel) => ({
			...oldModel,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	const handleCancelClick = (cancelId) => {
		setRowModesModel({
			...rowModesModel,
			[cancelId]: { mode: GridRowModes.View, ignoreModifications: true },
		});
		setCycleOverlapError(false);
		setAssocAlreadySelected(null);
		setRows((prev) => prev.filter((row) => row.entryDate)); // keep only rows existing in DB.
		newlyAddedRow.current = null;
	};

	const handleSaveClick = (saveId) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => row[key].error);

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[saveId]: { mode: GridRowModes.View },
			}));
		} else if (errors?.length === keys.length) {
			apiRef.current.setState((state) => ({
				...state,
				editRows: {
					...state.editRows,
					[saveId]: {
						...state.editRows[saveId],
						...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[saveId][key], error: true } }), {}),
					},
				},
			}));
		}
	};

	const handleDeleteClick = async (ids) => {
		// Set row loading to true
		setRowLoading(true);

		ids?.forEach((eleId) => {
			RowFeedback.skeleton(eleId, apiRef).delete().display();
		});

		const selectedId = props?.selectedItem?._id?.includes("_") ? props?.selectedItem?._id.split("_")[1] : props?.selectedItem?._id;
		deleteTemporaryGuaranteedItemHandler(
			{
				locationId,
				listId: selectedId,
				itemIds: ids,
				commissionCycleId: props?.commissionCycleId,
				department: identifiers.department,
			},
			{
				onSuccess: () => {
					setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
					setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
					setTableRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				},
				onError: () => {
					ids.forEach((id) => {
						RowFeedback.skeleton(id, apiRef).errorDelete();
					});
				},
				onSettled: () => {
					setRowLoading(false);
				},
			}
		);
	};

	// This function is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	const showUpdateErrors = (rowId) => {
		const rowEdit = apiRef.current.state.editRows;
		rowEdit[rowId].effectiveDateFromItem.error = true;
		rowEdit[rowId].effectiveDateToItem.error = true;
		const fields = Object.keys(rowEdit[rowId]);

		fields.forEach((field) => {
			const value = rowEdit[rowId][field];
			if (field === "amount") {
				apiRef.current.setEditCellValue({ field, value, id: rowId, debounceMs: 200 });
			}
			if (field.includes("effectiveDate")) {
				field === "effectiveDateFromItem" &&
					apiRef.current.setEditCellValue({ field, value: effecDateFrom, id: rowId, debounceMs: 200 });
				field === "effectiveDateToItem" &&
					apiRef.current.setEditCellValue({ field, value: effecDateTo, id: rowId, debounceMs: 200 });
			}
			if (field === "nameAssociate") {
				apiRef.current.setEditCellValue({ field, value: value.value, id: rowId, debounceMs: 200 });
			}
		});

		setRowModesModel((prev) => ({
			...prev,
			[rowId]: { mode: GridRowModes.Edit },
		}));

		setCycleOverlapError(true);
		setAssocAlreadySelected(rowEdit[rowId]);
	};

	const sanitizeInput = (input) => ({
		...input,
		addedBy: input.addedBy?._id,
		nameAssociate: input.nameAssociate?._id,
		effectiveDateFromItem: moment.isMoment(input.effectiveDateFromItem)
			? input.effectiveDateFromItem
			: moment(input.effectiveDateFromItem),
		effectiveDateToItem: moment.isMoment(input.effectiveDateToItem) ? input.effectiveDateToItem : moment(input.effectiveDateToItem),
	});

	const createMutateOperation = (mutateId, apiRefObj, resolve) => ({
		onSuccess: (response) => {
			RowFeedback.skeleton(mutateId, apiRefObj).success();
			if (response === null) {
				showUpdateErrors(mutateId);
				return;
			}
			setRows(response?.items || []);
			setTableRows(response?.items || []);
			resolve?.(response);
		},
		onError: (err) => {
			RowFeedback.skeleton(mutateId, apiRefObj).error();
			errorLogger(err);
		},
		onSettled: () => {
			setRowLoading(false);
		},
	});

	const handleNewRow = (input) => {
		newlyAddedRow.current = { ...input };
		return new Promise((resolve) => {
			addTemporaryGuaranteedItemHandler(
				{
					input,
					commissionCycleId: props?.commissionCycleId,
					unitListId: getNormalizedCommissionCycle(props?.selectedItem?._id),
					department: identifiers.department,
				},
				createMutateOperation(input._id, apiRef, resolve)
			);
		});
	};

	const handleOldRow = (input) => {
		newlyAddedRow.current = { ...input };
		delete input.lastModifiedOn;
		return new Promise((resolve) => {
			const id = props?.selectedItem?._id?.includes("_") ? props?.selectedItem?._id.split("_")[1] : props?.selectedItem?._id;
			const selectedUnitItemId = props?.selectedItem?.temporaryGuaranteedId || id;
			updatTemporaryGuaranteedItemHandler(
				{
					input,
					locationId,
					commissionCycleId: props?.commissionCycleId,
					unitItemId: selectedUnitItemId,
					department: identifiers.department,
				},
				createMutateOperation(input._id, apiRef, resolve)
			);
		});
	};

	const handleProcessRowUpdate = async (newRow, oldRow) => {
		const isNew = !newRow.entryDate;

		if (isNew) {
			newRow.entryDate = moment();
		} else {
			const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
			if (!isModified) {
				return oldRow;
			}
		}

		const sanitizedInput = sanitizeInput(newRow);
		setRowLoading(true);
		RowFeedback.skeleton(newRow._id, apiRef).display();

		if (isNew) {
			await handleNewRow(sanitizedInput);
		} else {
			await handleOldRow(sanitizedInput);
		}

		setRows((prev) => prev.map((row) => (row._id === newRow._id ? newRow : row)));
		setEffecDateFrom(null);
		setEffecDateTo(null);

		return newRow;
	};

	const cleanUpEditionMode = (item, operation, wasDeletedRowInEditMode) => {
		if (item && operation === "delete") {
			if (wasDeletedRowInEditMode) {
				setRowModesModel({});
			} else {
				setRowModesModel((prev) => ({
					...prev,
					[item._id]: { mode: GridRowModes.Edit },
				}));
			}
		}
		newlyAddedRow.current = null;
	};

	const handleRowEditStart = (_params, event) => {
		if (props?.selectedItem?.commissionCycleStatus !== "ACTIVE" && props?.selectedItem?.commissionCycleStatus !== "STAGING") {
			event.defaultMuiPrevented = true;
		}

		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (_params, event) => {
		event.defaultMuiPrevented = true;
	};

	const columns = [
		{ field: "_id", headerName: "ID", width: 50, hide: true, filterable: false },
		{
			field: "updatedAt",
			headerName: "Last Updated At",
			width: 160,
			type: "date",
			sortable: true,
			editable: false,
			resizable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.abbrDatePaddedTime12h),
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "addedBy",
			headerName: "Updated By",
			sortable: true,
			minWidth: ONE_HUNDRED_FIFTY_FIVE,
			maxWidth: ONE_HUNDRED_FIFTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			valueFormatter: (value) => value?.fullName,
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "nameAssociate",
			headerName: "Assoc. Name",
			minWidth: ONE_HUNDRED_SIXTY,
			maxWidth: ONE_HUNDRED_SIXTY * COLUMN_RESIZE_FACTOR,
			sortable: true,
			editable: shouldEdit(),
			resizable: true,
			renderEditCell: (params) => (
				<InputEditUserCell
					{...params}
					rows={rows}
					department={identifiers.department}
					error={!params.row.userId || params.error}
					cycleOverlapError={cycleOverlapError}
					setCycleOverlapError={setCycleOverlapError}
					setAssocAlreadySelected={setAssocAlreadySelected}
				/>
			),
			valueFormatter: (value) => value?.fullName ?? "",
			renderCell: ({ formattedValue }) => <CustomCell value={formattedValue} hasTooltip />,
		},
		{
			field: "effectiveDateFromItem",
			headerName: "Effective From ",
			width: ONE_HUNDRED_FIFTY_FIVE,
			type: "date",
			editable: shouldEdit(),
			sortable: true,
			resizable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderEditCell: (params) => (
				<InputEditDatetimeCell
					{...params}
					masterPermission={props?.masterPermission}
					startDate={getDateInUTC(props?.selectedItem?.effectiveDateFrom)}
					endDate={getDateInUTC(props?.selectedItem?.effectiveDateTo)}
					assocAlreadySelected={assocAlreadySelected}
					type={"from"}
					fromDate={effecDateFrom}
					effecDateTo={effecDateTo}
					setEffecDateFrom={setEffecDateFrom}
					setEffecDateTo={setEffecDateTo}
					selectedItem={props?.selectedItem}
					cycleOverlapError={cycleOverlapError}
					setCycleOverlapError={setCycleOverlapError}
					apiRef={apiRef}
					queryClient={queryClient}
					locationId={locationId}
					commissionCycleId={props?.commissionCycleId}
					department={identifiers.department}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value || moment(effecDateFrom));
					const valueIsDate = dateValidation(params.props.value || moment(effecDateFrom));
					const valueOverlaps = overlapsValidation({
						effectiveDateFrom: assocAlreadySelected?.effectiveDateFromItem,
						effectiveDateTo: assocAlreadySelected?.effectiveDateToItem,
						rowEffeDateFrom: effecDateFrom,
						rowEffeDateTo: effecDateTo,
					});
					return { ...params.props, error: !valueExists || !valueIsDate || valueOverlaps, valueOverlaps };
				}
				return params.props;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} />,
		},
		{
			field: "effectiveDateToItem",
			headerName: "Effective To ",
			width: 120,
			type: "date",
			editable: shouldEdit(),
			sortable: true,
			resizable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderEditCell: (params) => (
				<InputEditDatetimeCell
					{...params}
					assocAlreadySelected={assocAlreadySelected}
					startDate={getDateInUTC(props?.selectedItem?.effectiveDateFrom)}
					endDate={getDateInUTC(props?.selectedItem?.effectiveDateTo)}
					type={"to"}
					fromDate={params.row.effectiveDateFromItem}
					setEffecDateFrom={setEffecDateFrom}
					setEffecDateTo={setEffecDateTo}
					effecDateTo={effecDateTo}
					cycleOverlapError={cycleOverlapError}
					setCycleOverlapError={setCycleOverlapError}
					apiRef={apiRef}
					queryClient={queryClient}
					locationId={locationId}
					commissionCycleId={props?.commissionCycleId}
					disabled={!moment(params.row.effectiveDateFromItem).isValid()}
					department={identifiers.department}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value || moment(effecDateTo));
					const valueIsDate = dateValidation(params.props.value || moment(effecDateTo));
					const valueOverlaps = overlapsValidation({
						effectiveDateFrom: assocAlreadySelected?.effectiveDateFromItem,
						effectiveDateTo: assocAlreadySelected?.effectiveDateToItem,
						rowEffeDateFrom: effecDateFrom,
						rowEffeDateTo: effecDateTo,
					});
					const isDateToBeforeDateFrom = moment(params.props.value).isBefore(moment(effecDateFrom));
					return {
						...params.props,
						error: !valueExists || !valueIsDate || isDateToBeforeDateFrom || valueOverlaps,
						valueOverlaps,
					};
				}
				return params.props;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} />,
		},
		{
			field: "payoutFrequency",
			headerName: "Frequency",
			width: ONE_HUNDRED_FIFTY_FIVE,
			editable: shouldEdit(),
			renderEditCell: (params) => {
				const { id, field, row } = params;
				const associatePosition = row.nameAssociate?.position ?? "";
				const drawModality = drawModalities.find(({ position }) => associatePosition.includes(position))?.drawModality;
				return (
					<PayoutFrequencySelector
						{...params}
						drawModality={drawModality}
						loading={isLoadingDrawModalities}
						onChange={(value) => apiRef.current.setEditCellValue({ id, field, value })}
					/>
				);
			},
			valueFormatter: (value) => (value ? PayoutFrequencyOptions[value].label : ""),
			renderCell: ({ formattedValue }) => <CustomCell value={formattedValue} />,
		},
		{
			field: "amount",
			headerName: "$ Amount",
			width: 130,
			editable: shouldEdit(),
			align: "right",
			renderEditCell: (params) => {
				const { id, field, hasFocus, value } = params;
				return (
					<TableInputValue
						{...params}
						isControlledApi
						isEditingMode={hasFocus}
						isRequiredOnChange
						data={[
							{
								type: "Dollar",
								min: 0.01,
								max: 100000,
								decimalsEditable: true,
								decimals: 2,
							},
						]}
						value={{ value, input: "Dollar" }}
						defaultType={"Dollar"}
						sx={{ height: "27px", width: "115px" }}
						onBlur={(updated) => apiRef.current.setEditCellValue({ id, field, value: +updated })}
						error={!value}
					/>
				);
			},
			cellClassName: "no-focus cell-with-dollar-symbol",
			resizable: false,
			valueFormatter: (value = 0) => accountFormat(value),
			preProcessEditCellProps: (params) => {
				const valueError = typeof params.props.value === "object" ? !params.props.value.value : !params.props.value;
				return { ...params.props, error: valueError };
			},
		},
		{
			field: "actions",
			type: "actions",
			width: 70,
			resizable: false,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
				return isInEditMode
					? [
							<GridActionsCellItem
								key="saveTemporary"
								icon={<SaveIconComponent />}
								label="Save"
								onClick={() => handleSaveClick(id)}
							/>,
							<GridActionsCellItem
								key="cancel"
								icon={<CancelIcon />}
								label="Cancel"
								className="textPrimary"
								onClick={() => handleCancelClick(id)}
								color="inherit"
							/>,
					  ]
					: [];
			},
		},
	];

	const isInvalidCCStatus = and(
		props?.selectedItem?.commissionCycleStatus !== "ACTIVE",
		props?.selectedItem?.commissionCycleStatus !== "STAGING",
		!props?.masterPermission
	);
	const isComponentLoading = rowLoading || isLoading;

	useEffect(() => {
		if (typeof apiRef.current?.getAllRowIds === "function") {
			const allRows = apiRef.current.getAllRowIds();
			if (allRows?.length) {
				allRows.forEach((row) => {
					if (row) {
						apiRef.current.selectRow(row, false);
						setRowModesModel((prev) => ({
							...prev,
							[row]: { mode: GridRowModes.View },
						}));
					}
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiRef]);

	return (
		<DataSmartTable
			columns={columns}
			rows={rows ?? []}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			maxRows={14}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			autoHeight={false}
			emptyTableImageHeight="452px"
			isRowSelectable={() => {
				if (props?.selectedItem?.commissionCycleStatus !== "ACTIVE" && props?.selectedItem?.commissionCycleStatus !== "STAGING") {
					return false;
				}

				return !editModeActive && shouldEdit();
			}}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={console.error}
			onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{
				_id: false,
				actions: Object.values(rowModesModel).some(({ mode }) => mode === GridRowModes.Edit),
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					search: {
						columns,
						data: rows ?? [],
						hide: !!selectedRows.length,
						disabled: editModeActive,
						searchInNestedArrays: ["activityType", "actionType", "initiatedBy"],
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length,
						disabled: editModeActive || !editTempGuaranteed || isComponentLoading || isInvalidCCStatus,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove",
						hide: !selectedRows.length,
						disabled: editModeActive || !editTempGuaranteed || isComponentLoading,
					},
				},
				noRowsOverlay: {
					title: "No Auto Alerts Data",
				},
			}}
			hideFooter
			customProps={{
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.MuiDataGrid-main]:!mt-[14px]": true,
				},
				hide: !rows?.length,
				tableHidedProps: canAddItemsInTable
					? {
							currentMonth: true,
							sectionFirstTitle: "No items in this list.",
							onAddTheFirstOne: handleAddRow,
					  }
					: null,
			}}
		/>
	);
}

TemporaryGuaranteedTable.propTypes = {
	value: PropTypes.string,
	currentMonth: PropTypes.bool,
	canAddItems: PropTypes.bool,
	data: PropTypes.array,
	commissionCycleId: PropTypes.string,
	parentCommissionCycleId: PropTypes.string,
	selectedItem: PropTypes.shape({
		temporaryGuaranteedId: PropTypes.string,
		status: PropTypes.string,
		commissionCycleStatus: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		parentCommissionCycleId: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
		_id: PropTypes.string,
	}),
	id: PropTypes.number,
	field: PropTypes.string,
	startDate: PropTypes.instanceOf(Date),
	endDate: PropTypes.instanceOf(Date),
	fromDate: PropTypes.instanceOf(Date),
	effecDateTo: PropTypes.instanceOf(Date),
	type: PropTypes.string,
	setEffecDateFrom: PropTypes.func,
	setEffecDateTo: PropTypes.func,
	cycleOverlapError: PropTypes.bool,
	setCycleOverlapError: PropTypes.func,
	payPeriodsDate: PropTypes.array,
	masterPermission: PropTypes.bool,
};
